import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import SideMenu from '../components/SideMenu';
import { Plus, Menu, Search, X, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Customers = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    address: ''
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const res = await axiosInstance.get('/customers');
      setCustomers(res.data);
    } catch (err) {
      console.error('Error loading customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        // Update existing customer
        await axiosInstance.put(`/customers/${editingCustomer.id}`, formData);
        toast.success('Customer updated successfully!');
      } else {
        // Create new customer
        await axiosInstance.post('/customers', formData);
        toast.success('Customer added successfully!');
      }
      setShowAddForm(false);
      setEditingCustomer(null);
      setFormData({ name: '', mobile: '', email: '', address: '' });
      loadCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving customer');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || '',
      mobile: customer.mobile || '',
      email: customer.email || '',
      address: customer.address || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Are you sure you want to delete customer "${customer.name}"?\n\nNote: Customer can only be deleted if not used in any orders, bookings, or approvals.`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/customers/${customer.id}`);
      toast.success('Customer deleted successfully!');
      loadCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting customer');
    }
  };

  const handleCancelEdit = () => {
    setShowAddForm(false);
    setEditingCustomer(null);
    setFormData({ name: '', mobile: '', email: '', address: '' });
  };

  // Filter customers based on search query
  const filteredCustomers = useMemo(() => {
    if (!searchQuery.trim()) return customers;
    
    const query = searchQuery.toLowerCase();
    return customers.filter(customer => 
      customer.name?.toLowerCase().includes(query) ||
      customer.mobile?.toLowerCase().includes(query) ||
      customer.email?.toLowerCase().includes(query) ||
      customer.address?.toLowerCase().includes(query)
    );
  }, [customers, searchQuery]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="page-container">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setMenuOpen(true)} 
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors"
              data-testid="open-menu-button"
            >
              <Menu className="w-6 h-6 text-gray-700" />
            </button>
            <h1 className="page-title" data-testid="customers-page-title">Customers</h1>
          </div>
        </div>

        {!showAddForm && (
          <div className="mb-4 sm:mb-6 space-y-3">
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-primary flex items-center gap-2 w-full sm:w-auto"
              data-testid="add-customer-button"
            >
              <Plus className="w-5 h-5" />
              Add Customer
            </button>
            
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, mobile, email, or address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field w-full pl-10 pr-10"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            
            {/* Results count */}
            <p className="text-xs sm:text-sm text-gray-600">
              Showing {filteredCustomers.length} of {customers.length} customers
            </p>
          </div>
        )}

        {showAddForm && (
          <div className="premium-card p-4 sm:p-6 mb-4 sm:mb-6 fade-in">
            <h2 className="text-base sm:text-lg font-semibold mb-4">
              {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Mobile *</label>
                <input
                  type="tel"
                  className="input-field w-full"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  required
                  data-testid="customer-mobile-input"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  className="input-field w-full"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="customer-email-input"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Address</label>
                <textarea
                  className="input-field w-full"
                  rows="3"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="customer-address-input"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="btn-secondary flex-1"
                  data-testid="cancel-add-customer"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1" data-testid="submit-customer">
                  {editingCustomer ? 'Update Customer' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-600 mx-auto"></div>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm sm:text-base">
              {searchQuery ? 'No customers found matching your search' : 'No customers yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4 fade-in">
            {filteredCustomers.map((customer) => (
              <div key={customer.id} className="premium-card p-4 sm:p-5" data-testid={`customer-card-${customer.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="font-bold text-base sm:text-lg gold-text">{customer.name}</h3>
                    <div className="text-xs sm:text-sm text-gray-700 mt-2 space-y-1">
                      <p><strong>Mobile:</strong> {customer.mobile}</p>
                      {customer.email && <p><strong>Email:</strong> {customer.email}</p>}
                      {customer.address && <p><strong>Address:</strong> {customer.address}</p>}
                    </div>
                  </div>
                  
                  {/* Edit and Delete Buttons */}
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleEdit(customer)}
                      className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors"
                      title="Edit customer"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(customer)}
                      className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                      title="Delete customer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <SideMenu user={user} isOpen={menuOpen} onClose={() => setMenuOpen(false)} onLogout={onLogout} />
    </div>
  );
};

export default Customers;
